import { Group, Stack } from '@mantine/core'
import Select from 'app/views/components/inputs/Select'
import TextArea from 'app/views/components/inputs/TextArea'
import { UseFormReturnType } from '@mantine/form'
import { DeliverySettings } from 'app/models/entity-params'
import { roleLabels, thirdPartyProviderRoleOptions } from './entity-profile/components/entity-profile-form/roles-section'
import { deliveryMethodOptions } from 'app/models/dropdown-options'
import { EntityProfileParams, EntityRole, ThirdPartyRoles } from 'app/models/entity-profile-params'
import IonIcon from '@reacticons/ionicons'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import FormWrapper from 'app/views/components/Form/FormWrapper'
import { useEffect, useRef } from 'react'

type EntitiesFormProps = {
    entityProfiles?: EntityProfileParams[]
    form: UseFormReturnType<any>
    isLoading: boolean
    isStepper?: boolean
    submitForm: any
    submitAndExit: () => void
}

export default function EntitiesFormDeliverySettings({
    entityProfiles,
    form,
    isLoading,
    submitAndExit,
}: EntitiesFormProps) {

    const hasInsertedInitial = useRef(false);

    useEffect(() => {
        if (!hasInsertedInitial.current && form.values.deliverySettings.length === 0) {
            form.insertListItem('deliverySettings', {
                deliveryRole: '',
                deliveryMethod: '',
                deliveryEmails: '',
            });
            hasInsertedInitial.current = true;
        }
    }, []);

    const getEntityProfileOptions = (): { label: string; value: ThirdPartyRoles }[] => {
        if (!entityProfiles || !form.values.entityProfile) return [];

        const selectedProfileIds = form.values.entityProfile;

        const availableRoles = new Set<EntityRole>();

        selectedProfileIds.forEach((profileId: string) => {
            const profile = entityProfiles.find(p => p.id === profileId);
            if (profile) {
                profile.roles.forEach(role => availableRoles.add(role));
            }
        });

        const allowedRoles = new Set(thirdPartyProviderRoleOptions.map(option => option.value));

        const filteredRoles = Array.from(availableRoles).filter(role => allowedRoles.has(role));

        const options = filteredRoles.map(role => ({
            label: roleLabels[role] || role,
            value: role as string as ThirdPartyRoles,
        }));

        // The Admin Agent profile role maps onto the Agent delivery role — the
        // only role pulled for the Open Trade Packet EML recipients, and
        // excluded from the Settlement Packet (ADR_LOS-026).
        if (availableRoles.has(EntityRole.AdminAgentRole)) {
            options.push({ label: 'Agent', value: ThirdPartyRoles.AgentRole });
        }

        return options;
    };

    const getAvailableRolesForRow = (currentIndex: number): { label: string; value: ThirdPartyRoles; disabled?: boolean }[] => {
        const allOptions = getEntityProfileOptions();

        // Get all selected roles except from the current row
        const selectedRoles = form.values.deliverySettings
            .map((setting: DeliverySettings, index: number) =>
                index !== currentIndex ? setting.deliveryRole : null
            )
            .filter((role: ThirdPartyRoles | null) => role !== null);

        // Mark roles as disabled if they're already selected in other rows
        return allOptions.map(option => ({
            ...option,
            disabled: selectedRoles.includes(option.value),
        }));
    };

    type SettingError = {
        deliveryRole?: string;
        deliveryMethod?: string;
        deliveryEmails?: string;
    };

    type FormErrors = {
        deliverySettings?: SettingError[];
    };

    const fields = form.values?.deliverySettings?.map((setting: DeliverySettings, index: number) => {
        const profileOptions = getAvailableRolesForRow(index);
        const noProfiles = profileOptions.length === 0;

        const errors = form.errors as FormErrors;

        const profileError = noProfiles || errors.deliverySettings?.[index]?.deliveryRole;
        const deliveryMethodError = errors.deliverySettings?.[index]?.deliveryMethod;
        const emailError = errors.deliverySettings?.[index]?.deliveryEmails;

        return (
            <div key={'deliverySettingsForm' + index}>
                <form>
                    <Stack>
                        <Group noWrap w="100%" align="flex-start">
                            <Select
                                label="Entity Profile"
                                placeholder={noProfiles ? 'No Profiles Found' : 'Select Profile'}
                                searchable
                                w="33.33%"
                                data={profileOptions}
                                {...form.getInputProps(`deliverySettings.${index}.deliveryRole`)}
                                error={profileError}
                            />

                            <Select
                                label="Delivery Method"
                                placeholder="Select Method"
                                searchable
                                w="33.33%"
                                data={deliveryMethodOptions}
                                {...form.getInputProps(`deliverySettings.${index}.deliveryMethod`)}
                                error={deliveryMethodError}
                            />

                            <TextArea
                                label="Email Addresses"
                                placeholder="email1@example.com; email2@example.com"
                                w="33.33%"
                                minRows={1}
                                sx={{
                                    textarea: {
                                        minHeight: '60px !important',
                                        height: '60px !important',
                                    }
                                }}
                                {...form.getInputProps(`deliverySettings.${index}.deliveryEmails`)}
                                error={emailError}
                            />

                            {(form.values.deliverySettings.length > 0) &&
                                <Group noWrap mt='25px'>
                                    <Group noWrap w={'75%'}></Group>
                                    <Group noWrap w={'100%'}>
                                        <IonIcon
                                            name="trash-outline"
                                            className="click-ion-icon"
                                            style={{ color: "red" }}
                                            onClick={() => form.removeListItem('deliverySettings', index)}
                                        />
                                        <span className="remove-button-text">Remove</span>
                                    </Group>
                                </Group>
                            }
                        </Group>
                    </Stack>
                </form>
                {(form.values.deliverySettings.length > 1) &&
                    <Group noWrap className="form-row-divider"></Group>
                }
            </div>
        )
    })

    return (
        <FormWrapper title="Delivery Settings">
            <div className="content">
                <>{fields}</>

                <Stack style={{ marginTop: form.values.deliverySettings.length > 0 ? '15px' : '0px' }}>
                    <Group noWrap>
                        <div className="add-action-container">
                            <IonIcon
                                name="add-circle-outline"
                                className="click-ion-icon"
                                onClick={() =>
                                    form.insertListItem('deliverySettings', {
                                        deliveryRole: '',
                                        deliveryMethod: '',
                                        deliveryEmails: '',
                                    })
                                }
                            />
                            <span className="add-action-text">Add Delivery Setting</span>
                        </div>
                    </Group>
                </Stack>

                <Stack>
                    <div
                        style={{
                            marginTop: '1rem',
                            display: 'flex',
                            gap: '.5rem',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <PrimaryButton
                            disabled={!form.isValid()}
                            className="form-button"
                            loading={isLoading}
                            onClick={() => submitAndExit()}
                            w="100%"
                        >
                            Save and Exit
                        </PrimaryButton>
                    </div>
                </Stack>
            </div>
        </FormWrapper>
    )
}
